import React, { useState } from 'react';
import Icons from '../../../../assets/index';
import AddEditOptionPanel from './AddEditOptionPanel';
import AddEditFieldPanel from './AddEditFieldPanel';
import Panel from '../../../../components/Panel/Panel';
import Popup from '../../../../components/Popup/Popup';
import Button from '@/components/Button/Button';
export interface Field {
  id: string;
  label: string;
  fieldType: string;
  field_type?: string;
  required: boolean;
  additional_config: {
    editable: boolean;
    placeholder: string;
    defaultValue: string;
    autoPopulate?: boolean;
    helpText?: string;
    dataIdentifier?: string;
  };
}

export interface Option {
  id: string;
  label: string;
  value: string;
  fields: Field[];
}

interface Props {
  data: {
    options?: Option[];
    [key: string]: any;
  };
  sectionId: string;
  onSubmitUpdate: (sectionId: string, updatedField: any, nestedFieldId?: string) => void;
  setIsFieldOpen: (open: boolean) => void;
  setSelectedSection: (field: any) => void;
  onEditOption?: (option: Option, optionIdx: number) => void;
  onEditField?: (field: Field, optionIdx: number, fieldIdx: number) => void;
  fieldUpdateLoading?: boolean;
}

const CustomTableConditionWithMultipleFields: React.FC<Props> = ({
  data,
  sectionId,
  onSubmitUpdate,
  fieldUpdateLoading,
}) => {
  const [localOptions, setLocalOptions] = useState<Option[]>(
    (data.options || []).map((opt) => ({
      ...opt,
      fields: (opt.fields || []).map((field) => ({
        ...field,
        required: typeof field.required === 'boolean' ? field.required : true,
        additional_config: {
          ...field.additional_config,
          editable: field.additional_config?.editable ?? true,
          placeholder: field.additional_config?.placeholder ?? '',
          defaultValue: field.additional_config?.defaultValue ?? '',
        },
      })),
    }))
  );

  // Panel state
  const [optionPanel, setOptionPanel] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    option?: Option;
  } | null>(null);
  const [fieldPanel, setFieldPanel] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    optionId?: string;
    field?: Field;
  } | null>(null);

  // Delete confirmation state
  const [deleteOptionId, setDeleteOptionId] = useState<string | null>(null);
  const [deleteField, setDeleteField] = useState<{ optionId: string; fieldId: string } | null>(
    null
  );

  // const openAddOptionPanel = () => setOptionPanel({ open: true, mode: 'add' });
  const openEditOptionPanel = (option: Option) =>
    setOptionPanel({ open: true, mode: 'edit', option });
  const closeOptionPanel = () => setOptionPanel(null);

  const handleOptionPanelSave = (option: Option) => {
    let updatedOptions: Option[];
    const safeOption: Option = {
      ...option,
      fields: option.fields || [],
    };
    if (optionPanel?.mode === 'edit' && option.id) {
      safeOption.id = option.id;
      updatedOptions = localOptions.map((opt) =>
        opt.id === option.id
          ? { ...opt, label: option.label, value: option.value, fields: option.fields }
          : opt
      );
    } else {
      // For add mode, don't set ID - let backend generate it
      updatedOptions = [...localOptions, safeOption];
    }
    setLocalOptions(updatedOptions);
    onSubmitUpdate(sectionId, {
      ...data,
      options: updatedOptions,
      field_type: data.field_type || 'condition_with_multiple_fields',
    });
    closeOptionPanel();
  };

  // --- Field Handlers ---
  const openAddFieldPanel = (optionId: string) =>
    setFieldPanel({ open: true, mode: 'add', optionId });
  const openEditFieldPanel = (optionId: string, field: Field) =>
    setFieldPanel({ open: true, mode: 'edit', optionId, field });
  const closeFieldPanel = () => setFieldPanel(null);

  const handleFieldPanelSave = (field: Field) => {
    let updatedOptions: Option[] = localOptions;
    const safeField: Field = {
      ...field,
      required: field.required ?? true,
      additional_config: {
        ...field.additional_config,
        editable: field.additional_config?.editable ?? true,
        placeholder: field.additional_config?.placeholder ?? '',
        defaultValue: field.additional_config?.defaultValue ?? '',
      },
    };

    // For edit mode, ensure we have the field ID
    if (fieldPanel?.mode === 'edit' && field.id && fieldPanel.optionId) {
      safeField.id = field.id;
      updatedOptions = localOptions.map((opt) =>
        opt.id === fieldPanel.optionId
          ? {
              ...opt,
              fields: opt.fields.map((f) => (f.id === field.id ? safeField : f)),
            }
          : opt
      );
    } else if (fieldPanel?.mode === 'add' && fieldPanel.optionId) {
      // For add mode, don't set ID - let backend generate it
      updatedOptions = localOptions.map((opt) =>
        opt.id === fieldPanel.optionId
          ? {
              ...opt,
              fields: [...opt.fields, safeField],
            }
          : opt
      );
    }
    setLocalOptions(updatedOptions);
    onSubmitUpdate(sectionId, {
      ...data,
      options: updatedOptions,
      field_type: data.field_type || 'condition_with_multiple_fields',
    });
    closeFieldPanel();
  };

  // Delete Option
  const handleDeleteOption = (optionId: string) => {
    setDeleteOptionId(optionId);
  };
  const confirmDeleteOption = () => {
    if (!deleteOptionId) return;
    const updatedOptions = localOptions.filter((option) => option.id !== deleteOptionId);
    setLocalOptions(updatedOptions);
    onSubmitUpdate(sectionId, {
      ...data,
      options: updatedOptions,
      field_type: data.field_type || 'condition_with_multiple_fields',
    });
    setDeleteOptionId(null);
  };
  const cancelDeleteOption = () => setDeleteOptionId(null);

  // Delete Field
  const handleDeleteField = (optionId: string, fieldId: string) => {
    setDeleteField({ optionId, fieldId });
  };
  const confirmDeleteField = () => {
    if (!deleteField) return;
    const updatedOptions = localOptions.map((option) =>
      option.id === deleteField.optionId
        ? { ...option, fields: option.fields.filter((field) => field.id !== deleteField.fieldId) }
        : option
    );
    setLocalOptions(updatedOptions);
    onSubmitUpdate(sectionId, {
      ...data,
      options: updatedOptions,
      field_type: data.field_type || 'condition_with_multiple_fields',
    });
    setDeleteField(null);
  };
  const cancelDeleteField = () => setDeleteField(null);

  // --- Render ---
  // const renderFieldValue = (field: Field) => {
  //   const value = field.additional_config?.defaultValue;
  //   if (field.fieldType === 'text_area') {
  //     return (
  //       <div className="max-h-20 overflow-y-auto">
  //         {value || <span className="text-gray-400">Empty</span>}
  //       </div>
  //     );
  //   }
  //   return value || <span className="text-gray-400">Empty</span>;
  // };

  return (
    <div className="w-full">
      {/* Field Name/Label */}
      {data.label && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">{data.label}</label>
        </div>
      )}
      {/* Add Option Button
      <div className="mb-4 flex items-center gap-2">
        <button
          className="flex items-center gap-1 text-sm text-green-600 border border-green-600 rounded px-2 py-1"
          onClick={openAddOptionPanel}
        >
          <Icons.plusIcon /> Add Option
        </button>
      </div> */}
      {localOptions.map((option, optionIdx) => (
        <div
          key={option.id}
          className={`${optionIdx < localOptions.length - 1 ? 'mb-6' : ''} border overflow-hidden`}
        >
          <>
            <div className="flex justify-between items-center bg-[#DAF5EA] py-2 px-3">
              <div className="font-semibold">
                Option {optionIdx + 1}: {option.label}
              </div>
              <div className="flex gap-3">
                <button
                  className="flex items-center gap-1 text-sm text-ds-blue-600"
                  onClick={() => openEditOptionPanel(option)}
                >
                  <Icons.editIcon />
                </button>
                <button
                  className="flex items-center gap-1 text-sm text-red-600"
                  onClick={() => handleDeleteOption(option.id)}
                >
                  <Icons.trashIcon />
                </button>
                <Button
                  className="w-full"
                  onClick={() => openAddFieldPanel(option.id)}
                  startIcon={<Icons.plusIcon color="white" />}
                  color="primary"
                >
                  Add Action Field
                </Button>
              </div>
            </div>
            {option.fields.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="bg-[#27528B] text-left text-white">
                    <th className="py-2 px-3">Field Name</th>
                    <th className="py-2 px-3">Field Type</th>
                    {/* <th className="py-2 px-3">Value</th> */}
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {option.fields.map((field, idx) => (
                    <tr
                      key={field.id}
                      className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                    >
                      <td className="py-2 px-3">{field.label || '-'}</td>
                      <td className="py-2 px-3">{field?.fieldType || field?.field_type || '-'}</td>
                      {/* <td className="py-2 px-3">{renderFieldValue(field)}</td> */}
                      <td className="py-2 px-3 text-right">
                        <button
                          className="mr-3 text-blue-600"
                          onClick={() => openEditFieldPanel(option.id, field)}
                          disabled={fieldUpdateLoading}
                        >
                          <Icons.editIcon />
                        </button>
                        <button
                          className="text-red-600"
                          onClick={() => handleDeleteField(option.id, field.id)}
                          disabled={fieldUpdateLoading}
                        >
                          <Icons.trashIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No fields added to this option yet
              </div>
            )}
          </>
        </div>
      ))}

      {/* <div>
      <p className="text-md text-ds-gray-500">
        Are you sure you want to delete this section? Deleting it will remove all fields within this
        section permanently. This action cannot be undone.
      </p>

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={closePopUp} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="error" type="button" onClick={handleDelete} loading={isLoading}>
          Delete
        </Button>
      </div>
    </div> */}
      {/* Delete Option Confirmation Popup */}
      {deleteOptionId && (
        <Popup isOpen={!!deleteOptionId} title="Delete Option" onClose={cancelDeleteOption}>
          <p className="text-md text-ds-gray-500">
            <p>Are you sure you want to delete this option?</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={cancelDeleteOption}>Cancel</Button>
              <Button
                loading={fieldUpdateLoading}
                color="error"
                type="button"
                onClick={confirmDeleteOption}
              >
                Delete
              </Button>
            </div>
          </p>
        </Popup>
      )}
      {/* Delete Field Confirmation Popup */}
      {deleteField && (
        <Popup isOpen={!!deleteField} title="Delete Field" onClose={cancelDeleteField}>
          <div className="text-md text-ds-gray-500">
            <p>Are you sure you want to delete this field?</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={cancelDeleteField}>Cancel</Button>
              <Button
                loading={fieldUpdateLoading}
                color="error"
                type="button"
                onClick={confirmDeleteField}
              >
                Delete
              </Button>
            </div>
          </div>
        </Popup>
      )}
      {/* Option Panel */}
      {optionPanel?.open && (
        <Panel
          isOpen={optionPanel.open}
          onClose={closeOptionPanel}
          title={optionPanel.mode === 'edit' ? 'Edit Option' : 'Add Option'}
          size="medium"
        >
          <AddEditOptionPanel
            mode={optionPanel.mode}
            option={optionPanel.option}
            onSave={handleOptionPanelSave}
            onCancel={closeOptionPanel}
          />
        </Panel>
      )}
      {/* Field Panel */}
      {fieldPanel?.open && (
        <Panel
          isOpen={fieldPanel.open}
          onClose={closeFieldPanel}
          title={fieldPanel.mode === 'edit' ? 'Edit Field' : 'Add Field'}
          size="veryLarge"
        >
          <AddEditFieldPanel
            mode={fieldPanel.mode}
            field={fieldPanel.field}
            onSave={handleFieldPanelSave}
            onCancel={closeFieldPanel}
          />
        </Panel>
      )}
    </div>
  );
};

export default CustomTableConditionWithMultipleFields;
